import { AccountingAccountTypeEnum } from '@zaoblako/shared/modules/catalog/enums/accounting-account-type.enum';
import { AccountingSubAccountFindDto, type AccountingSubAccountFindInterface } from '@zaoblako/shared/modules/catalog/dtos/accounting-account/accounting-sub-account-find.dto';
import { Connection1cFindDto, type Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';

import * as yup from 'yup';

/** Accounting account response body - DTO signature */
export interface AccountingAccountFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Sub-account creation date and type */
  created: Date;
  /** Sub-account last update date and type */
  updated: Date;
  /** Sub-account soft deletion date and type */
  deleted?: Date;
  /** Account display name */
  name: string;
  /** Account unique code */
  code: string;
  /** Account type */
  type: AccountingAccountTypeEnum;
  /** Account quick selection code (sanitized {@link AccountingAccountEntity.code} value) */
  quickSelectionCode: string;
  /** Is currency accounting maintained? */
  currency?: boolean;
  /** Is quantitative accounting maintained? */
  quantitative?: boolean;
  /** Is accounting for divisions maintained?  */
  accountingForDivisions?: boolean;
  /** Is tax accounting maintained? */
  taxAccounting?: boolean;
  /** Sub-accounts belong to this account */
  subAccounts?: AccountingSubAccountFindInterface[];
  /** 1C ERP databases that use this account */
  connections?: Connection1cFindInterface[];
}

/** Accounting account response body - DTO schema */
export const AccountingAccountFindDto = SerdeDtoClass.create<AccountingAccountFindInterface>(CatalogDtosKeysEnum.ACCOUNTING_ACCOUNT_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date()
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date()
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date()
  },
  name: {
    sortable: true,
    label: 'Наименование счета',
    serializer: yup.string(),
  },
  code: {
    sortable: true,
    label: 'Код счета',
    serializer: yup.string(),
  },
  type: {
    sortable: true,
    label: 'Тип счета',
    serializer: yup
      .string()
      .oneOf(Object.values(AccountingAccountTypeEnum)),
  },
  quickSelectionCode: {
    sortable: true,
    label: 'Быстрый набор',
    serializer: yup.string(),
  },
  currency: {
    optional: true,
    label: 'Ведется валютный учет',
    serializer: yup.boolean(),
  },
  quantitative: {
    optional: true,
    label: 'Ведется количественный учет',
    serializer: yup.boolean(),
  },
  accountingForDivisions: {
    optional: true,
    label: 'Ведется учет по подразделениям',
    serializer: yup.boolean(),
  },
  taxAccounting: {
    optional: true,
    label: 'Ведется налоговый учет',
    serializer: yup.boolean(),
  },
  subAccounts: {
    optional: true,
    nested: AccountingSubAccountFindDto,
    label: 'Субконто',
    serializer: (_, nested) => yup.array(nested),
  },
  connections: {
    optional: true,
    label: 'В каких базах 1С используется',
    nested: Connection1cFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
